import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash,
  PencilSimple,
  Lock,
  FileText,
  ClipboardText,
  Question,
  ChatsCircle,
  CheckCircle,
  PaperPlaneTilt,
  Eye,
  VideoCamera,
  UsersThree,
  CaretLeft,
  CaretRight,
  ListNumbers,
} from "@phosphor-icons/react";
import api, { apiError, coverSrc } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import CourseMonogram from "@/components/CourseMonogram";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

export default function CourseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);

  const loadCourse = () => api.get(`/courses/${id}`).then((r) => setCourse(r.data)).catch(() => navigate("/app/cours"));
  useEffect(() => { loadCourse(); }, [id]);

  if (!course) return <div className="text-muted-foreground">Chargement...</div>;
  const canEdit = course.can_edit;
  const isStudent = user.role === "etudiant";

  return (
    <div className="space-y-6" data-testid="course-detail">
      <button onClick={() => navigate("/app/cours")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Retour aux cours
      </button>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="relative h-44">
          {coverSrc(course.cover_image) ? (
            <img src={coverSrc(course.cover_image)} alt={course.title} className="h-full w-full object-cover" />
          ) : (
            <CourseMonogram code={course.code} title={course.title} className="h-full w-full" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-4 left-6 text-white">
            {course.code && <p className="text-xs font-semibold uppercase tracking-wider">{course.code}</p>}
            <h1 className="font-display text-2xl font-extrabold sm:text-3xl">{course.title}</h1>
            <p className="text-sm opacity-90">{course.teacher_name} · {course.students_count} étudiants</p>
          </div>
          {canEdit && (
            <div className="absolute right-4 top-4 flex gap-2">
              <Button size="sm" variant={course.published ? "secondary" : "default"} className={course.published ? "" : "bg-emerald-600 hover:bg-emerald-700"}
                onClick={async () => { await api.post(`/courses/${id}/publish`); toast.success(course.published ? "Dépublié" : "Publié"); loadCourse(); }}
                data-testid="publish-btn">
                {course.published ? "Dépublier" : "Publier"}
              </Button>
            </div>
          )}
        </div>
        <div className="p-6">
          <p className="text-sm leading-relaxed text-muted-foreground">{course.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {course.meeting_url && (
              <a href={course.meeting_url} target="_blank" rel="noreferrer">
                <Button className="bg-accent hover:bg-accent/90" data-testid="join-meeting"><VideoCamera size={16} className="mr-2" weight="fill" /> Rejoindre la salle</Button>
              </a>
            )}
            {canEdit && <EditCourse course={course} onSaved={loadCourse} />}
            {canEdit && <CourseSettings course={course} onSaved={loadCourse} />}
          </div>
        </div>
      </div>

      <Tabs defaultValue="contenu">
        <TabsList className="flex-wrap">
          <TabsTrigger value="contenu" data-testid="tab-contenu"><FileText size={16} className="mr-1" /> Contenu</TabsTrigger>
          <TabsTrigger value="devoirs" data-testid="tab-devoirs"><ClipboardText size={16} className="mr-1" /> Devoirs</TabsTrigger>
          <TabsTrigger value="quiz" data-testid="tab-quiz"><Question size={16} className="mr-1" /> Quiz</TabsTrigger>
          <TabsTrigger value="forum" data-testid="tab-forum"><ChatsCircle size={16} className="mr-1" /> Forum</TabsTrigger>
        </TabsList>

        <TabsContent value="contenu"><Lessons courseId={id} canEdit={canEdit} isStudent={isStudent} /></TabsContent>
        <TabsContent value="devoirs"><Assignments courseId={id} canEdit={canEdit} isStudent={isStudent} /></TabsContent>
        <TabsContent value="quiz"><Quizzes courseId={id} canEdit={canEdit} isStudent={isStudent} /></TabsContent>
        <TabsContent value="forum"><Forum courseId={id} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- LESSONS ---------------- */
function Lessons({ courseId, canEdit, isStudent }) {
  const [lessons, setLessons] = useState([]);
  const [sections, setSections] = useState([]);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", type: "text", video_url: "", order: 0, section_id: "", available_from: "", require_previous: false });
  const [editId, setEditId] = useState(null);
  const [doneIds, setDoneIds] = useState([]);
  const [percent, setPercent] = useState(0);
  const [sectionOpen, setSectionOpen] = useState(false);
  const [sectionTitle, setSectionTitle] = useState("");
  const [editSectionId, setEditSectionId] = useState(null);

  const load = () => api.get(`/courses/${courseId}/lessons`).then((r) => {
    setLessons(r.data);
    setActive((a) => Math.min(a, Math.max(0, r.data.length - 1)));
  });
  const loadSections = () => api.get(`/courses/${courseId}/sections`).then((r) => setSections(r.data));
  const loadProgress = () => {
    if (!isStudent) return;
    api.get(`/courses/${courseId}/progress`).then((r) => { setDoneIds(r.data.completed_lesson_ids); setPercent(r.data.percent); }).catch(() => {});
  };
  useEffect(() => { load(); loadProgress(); loadSections(); }, [courseId]);

  const resetForm = () => { setForm({ title: "", content: "", type: "text", video_url: "", order: 0, section_id: "", available_from: "", require_previous: false }); setEditId(null); };
  const startEdit = (l) => { setForm({ title: l.title, content: l.content || "", type: l.type || "text", video_url: l.video_url || "", order: l.order, section_id: l.section_id || "", available_from: l.available_from || "", require_previous: !!l.require_previous }); setEditId(l.id); setOpen(true); };
  const save = async () => {
    const payload = { ...form, section_id: form.section_id || null, available_from: form.available_from || null };
    try {
      if (editId) { await api.put(`/lessons/${editId}`, payload); toast.success("Chapitre modifié"); }
      else { await api.post(`/courses/${courseId}/lessons`, { ...payload, order: lessons.length + 1 }); toast.success("Chapitre ajouté"); }
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/lessons/${id}`); toast.success("Chapitre supprimé"); load(); loadProgress(); };
  const toggleDone = async (lesson) => {
    const completed = !doneIds.includes(lesson.id);
    try {
      await api.post(`/lessons/${lesson.id}/complete`, { completed });
      const { data } = await api.get(`/courses/${courseId}/progress`);
      setDoneIds(data.completed_lesson_ids); setPercent(data.percent);
      if (completed) toast.success("Chapitre marqué comme terminé");
    } catch (e) { toast.error(apiError(e)); }
  };

  const openAddSection = () => { setSectionTitle(""); setEditSectionId(null); setSectionOpen(true); };
  const openEditSection = (s) => { setSectionTitle(s.title); setEditSectionId(s.id); setSectionOpen(true); };
  const saveSection = async () => {
    if (!sectionTitle.trim()) return;
    try {
      if (editSectionId) {
        const s = sections.find((x) => x.id === editSectionId);
        await api.put(`/sections/${editSectionId}`, { title: sectionTitle, order: s?.order || 0 });
      } else {
        await api.post(`/courses/${courseId}/sections`, { title: sectionTitle, order: sections.length + 1 });
      }
      setSectionOpen(false); loadSections();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delSection = async (id) => {
    if (!window.confirm("Supprimer cette section ? Les chapitres qu'elle contient seront conservés, juste détachés de la section.")) return;
    try { await api.delete(`/sections/${id}`); toast.success("Section supprimée"); loadSections(); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const grouped = useMemo(() => {
    const bySection = {};
    const noSection = [];
    lessons.forEach((l) => {
      if (l.section_id) { (bySection[l.section_id] = bySection[l.section_id] || []).push(l); }
      else noSection.push(l);
    });
    const groups = sections.map((s) => ({ section: s, items: bySection[s.id] || [] }));
    if (noSection.length > 0) groups.push({ section: null, items: noSection });
    return groups;
  }, [lessons, sections]);

  const current = lessons[active];
  const currentDone = current && doneIds.includes(current.id);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {canEdit && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild><Button variant="outline" data-testid="add-lesson-btn"><Plus size={16} className="mr-2" /> Ajouter un chapitre</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editId ? "Modifier le chapitre" : "Nouveau chapitre"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="lesson-title" /></div>
                <div>
                  <Label>Section (optionnel)</Label>
                  <Select value={form.section_id || "__none"} onValueChange={(v) => setForm({ ...form, section_id: v === "__none" ? "" : v })}>
                    <SelectTrigger data-testid="lesson-section"><SelectValue placeholder="Aucune section" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Aucune section</SelectItem>
                      {sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Contenu</Label><Textarea rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="lesson-content" /></div>
                <div><Label>Lien vidéo (optionnel)</Label><Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} /></div>
                <div className="rounded-lg border border-border p-3 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Restrictions d'accès (optionnel)</p>
                  <div>
                    <Label>Disponible à partir du</Label>
                    <Input type="date" value={form.available_from} onChange={(e) => setForm({ ...form, available_from: e.target.value })} data-testid="lesson-available-from" />
                  </div>
                  <label className="flex cursor-pointer items-start gap-2 text-sm">
                    <Checkbox checked={form.require_previous} onCheckedChange={(v) => setForm({ ...form, require_previous: !!v })} data-testid="lesson-require-previous" />
                    <span>
                      <span className="font-medium">Verrouillé tant que le chapitre précédent n'est pas terminé</span>
                    </span>
                  </label>
                </div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-lesson-btn">{editId ? "Enregistrer" : "Ajouter"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {canEdit && (
          <Dialog open={sectionOpen} onOpenChange={setSectionOpen}>
            <DialogTrigger asChild><Button variant="outline" onClick={openAddSection} data-testid="add-section-btn"><Plus size={16} className="mr-2" /> Nouvelle section</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editSectionId ? "Renommer la section" : "Nouvelle section"}</DialogTitle></DialogHeader>
              <div><Label>Titre</Label><Input value={sectionTitle} onChange={(e) => setSectionTitle(e.target.value)} placeholder="Semaine 1, Module 2 : Analyse SWOT..." data-testid="section-title" /></div>
              <DialogFooter><Button onClick={saveSection} className="bg-primary hover:bg-primary/90" data-testid="save-section-btn">{editSectionId ? "Enregistrer" : "Créer"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isStudent && lessons.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="progress-bar">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-medium">Ma progression</span>
            <span className="font-display font-bold text-primary" data-testid="progress-percent">{percent}%</span>
          </div>
          <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-secondary">
            <div className="absolute inset-0 flex" style={{ clipPath: `inset(0 ${100 - percent}% 0 0)` }}>
              <span className="h-full flex-1" style={{ background: "hsl(var(--campus-orange))" }} />
              <span className="h-full flex-1 bg-white" />
              <span className="h-full flex-1" style={{ background: "hsl(var(--campus-green))" }} />
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {doneIds.length} / {lessons.length} chapitres terminés
            {percent === 100 && " · Cours terminé 🎉"}
          </p>
        </div>
      )}

      {lessons.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="Aucun chapitre publié"
          description={canEdit ? "Ajoutez le premier chapitre pour que les étudiants puissent commencer à apprendre." : "Le contenu de ce cours n'est pas encore disponible."}
          actionLabel={canEdit ? "Ajouter un chapitre" : undefined}
          onAction={canEdit ? () => setOpen(true) : undefined}
        />
      ) : (
        <div className="grid gap-5 lg:grid-cols-[280px_1fr]" data-testid="chapter-layout">
          {/* Chapter navigation menu */}
          <aside className="lg:sticky lg:top-24 h-fit rounded-xl border border-border bg-card p-3" data-testid="chapter-nav">
            <p className="flex items-center gap-2 px-2 pb-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              <ListNumbers size={16} /> Chapitres
            </p>
            <nav className="space-y-3">
              {grouped.map((group, gi) => (
                <div key={group.section?.id || "sans-section"}>
                  {sections.length > 0 && (
                    <div className="group mb-1 flex items-center justify-between px-2">
                      <p className="text-xs font-bold uppercase tracking-wide text-foreground">{group.section?.title || "Sans section"}</p>
                      {canEdit && group.section && (
                        <div className="hidden gap-0.5 group-hover:flex">
                          <button onClick={() => openEditSection(group.section)} className="text-muted-foreground hover:text-primary"><PencilSimple size={12} /></button>
                          <button onClick={() => delSection(group.section.id)} className="text-muted-foreground hover:text-accent"><Trash size={12} /></button>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="space-y-1">
                    {group.items.map((l) => {
                      const i = lessons.findIndex((x) => x.id === l.id);
                      const isDone = doneIds.includes(l.id);
                      return (
                        <button
                          key={l.id}
                          onClick={() => setActive(i)}
                          data-testid={`chapter-link-${i}`}
                          className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                            i === active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                          } ${l.locked ? "opacity-60" : ""}`}
                        >
                          {l.locked ? (
                            <Lock size={18} weight="fill" className={i === active ? "text-primary-foreground" : "text-muted-foreground"} />
                          ) : isStudent && isDone ? (
                            <CheckCircle size={22} weight="fill" className={i === active ? "text-primary-foreground" : "text-emerald-600"} />
                          ) : (
                            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i === active ? "bg-primary-foreground text-primary" : "bg-secondary text-foreground"}`}>{i + 1}</span>
                          )}
                          <span className="line-clamp-2">{l.title.replace(/^Chapitre\s*\d+\s*:\s*/i, "")}</span>
                        </button>
                      );
                    })}
                    {group.items.length === 0 && <p className="px-3 py-1 text-xs text-muted-foreground">Aucun chapitre dans cette section.</p>}
                  </div>
                </div>
              ))}
            </nav>
          </aside>

          {/* Chapter content */}
          {current && (
            <div className="rounded-xl border border-border bg-card p-6 lg:p-8" data-testid="chapter-content">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
                    {sections.find((s) => s.id === current.section_id)?.title ? `${sections.find((s) => s.id === current.section_id).title} · ` : ""}
                    Chapitre {active + 1} / {lessons.length}
                  </p>
                  <h2 className="mt-1 font-display text-2xl font-extrabold tracking-tight">{current.title}</h2>
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => startEdit(current)} data-testid={`edit-lesson-${current.id}`}><PencilSimple size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(current.id)} data-testid={`del-lesson-${current.id}`}><Trash size={16} /></Button>
                  </div>
                )}
              </div>
              {current.locked ? (
                <div className="mt-5 flex flex-col items-center rounded-lg border border-dashed border-border py-12 text-center" data-testid="lesson-locked">
                  <Lock size={32} weight="fill" className="mb-3 text-muted-foreground" />
                  <p className="font-medium">Ce chapitre est verrouillé</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {current.lock_reason === "date"
                      ? `Disponible à partir du ${current.available_from}`
                      : "Terminez le chapitre précédent pour le débloquer."}
                  </p>
                </div>
              ) : (
                <>
                  {current.video_url && (
                    <a href={current.video_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:bg-accent/90">
                      <VideoCamera size={16} weight="fill" /> Regarder la vidéo
                    </a>
                  )}
                  <div className="mt-5 whitespace-pre-wrap text-[15px] leading-relaxed text-foreground">{current.content}</div>
                </>
              )}

              {isStudent && !current.locked && (
                <Button
                  onClick={() => toggleDone(current)}
                  className={`mt-6 ${currentDone ? "bg-emerald-600 hover:bg-emerald-700" : "bg-primary hover:bg-primary/90"}`}
                  data-testid="toggle-complete"
                >
                  <CheckCircle size={16} weight={currentDone ? "fill" : "regular"} className="mr-2" />
                  {currentDone ? "Chapitre terminé" : "Marquer comme terminé"}
                </Button>
              )}

              <div className="mt-8 flex items-center justify-between border-t border-border pt-5">
                <Button variant="outline" disabled={active === 0} onClick={() => setActive((a) => a - 1)} data-testid="prev-chapter">
                  <CaretLeft size={16} className="mr-1" /> Précédent
                </Button>
                <span className="text-sm text-muted-foreground">{active + 1} / {lessons.length}</span>
                <Button variant="outline" disabled={active >= lessons.length - 1} onClick={() => setActive((a) => a + 1)} data-testid="next-chapter">
                  Suivant <CaretRight size={16} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- ASSIGNMENTS ---------------- */
function Assignments({ courseId, canEdit, isStudent }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", due_date: "", max_points: 20, group_mode: false, rubric: [] });
  const [editId, setEditId] = useState(null);
  const [subOpen, setSubOpen] = useState(null);
  const [subText, setSubText] = useState("");
  const [subFile, setSubFile] = useState(null);
  const [subIsNew, setSubIsNew] = useState(true);
  const [gradeOpen, setGradeOpen] = useState(null);
  const [subs, setSubs] = useState([]);

  const load = () => api.get(`/courses/${courseId}/assignments`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [courseId]);

  const resetForm = () => { setForm({ title: "", description: "", due_date: "", max_points: 20, group_mode: false, rubric: [] }); setEditId(null); };
  const startEdit = (a) => { setForm({ title: a.title, description: a.description || "", due_date: a.due_date || "", max_points: a.max_points, group_mode: !!a.group_mode, rubric: a.rubric || [] }); setEditId(a.id); setOpen(true); };
  const save = async () => {
    try {
      if (editId) { await api.put(`/assignments/${editId}`, form); toast.success("Devoir modifié"); }
      else { await api.post(`/courses/${courseId}/assignments`, form); toast.success("Devoir créé"); }
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const addCriterion = () => setForm((f) => ({ ...f, rubric: [...f.rubric, { name: "", max_points: 5 }] }));
  const updateCriterion = (i, field, value) => setForm((f) => ({ ...f, rubric: f.rubric.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)) }));
  const removeCriterion = (i) => setForm((f) => ({ ...f, rubric: f.rubric.filter((_, idx) => idx !== i) }));
  const rubricTotal = form.rubric.reduce((sum, c) => sum + (Number(c.max_points) || 0), 0);
  const del = async (a) => {
    if (!window.confirm(`Supprimer le devoir "${a.title}" ? Les copies déjà rendues seront aussi supprimées.`)) return;
    try { await api.delete(`/assignments/${a.id}`); toast.success("Devoir supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const submit = async (aid) => {
    if (subIsNew && !subFile) { toast.error("Merci de joindre un fichier PDF ou Word avant d'envoyer."); return; }
    const fd = new FormData();
    fd.append("content", subText);
    if (subFile) fd.append("file", subFile);
    try {
      await api.post(`/assignments/${aid}/submit`, fd);
      toast.success("Devoir rendu");
      setSubOpen(null); setSubText(""); setSubFile(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const downloadAttachment = async (subId, filename) => {
    try {
      const res = await api.get(`/submissions/${subId}/attachment`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = filename || "piece-jointe";
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const openGrade = async (a) => { const r = await api.get(`/assignments/${a.id}/submissions`); setSubs(r.data); setGradeOpen(a); };

  return (
    <div className="space-y-4">
      {canEdit && (
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button variant="outline" data-testid="add-assignment-btn"><Plus size={16} className="mr-2" /> Nouveau devoir</Button></DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "Modifier le devoir" : "Nouveau devoir"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="assignment-title" /></div>
              <div><Label>Consignes</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="assignment-desc" /></div>
              <div className="flex gap-3">
                <div className="flex-1"><Label>Date limite</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                <div className="w-28">
                  <Label>Points</Label>
                  <Input type="number" value={form.rubric.length > 0 ? rubricTotal : form.max_points} disabled={form.rubric.length > 0} onChange={(e) => setForm({ ...form, max_points: Number(e.target.value) })} />
                </div>
              </div>
              <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-border p-3 text-sm">
                <Checkbox checked={form.group_mode} onCheckedChange={(v) => setForm({ ...form, group_mode: !!v })} data-testid="assignment-group-mode" />
                <span>
                  <span className="font-medium">Devoir de groupe</span>
                  <span className="block text-xs text-muted-foreground">Une seule remise par groupe (parmi les groupes ayant accès à ce cours) — tous les membres voient la même copie et la même note.</span>
                </span>
              </label>
              <div className="rounded-lg border border-border p-3">
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Grille de notation (optionnel)</Label>
                    <p className="text-xs text-muted-foreground">Décomposez la note en critères. Le total des points ci-dessus sera calculé automatiquement.</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {form.rubric.map((crit, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input value={crit.name} onChange={(e) => updateCriterion(i, "name", e.target.value)} placeholder="Ex : Fond, Forme, Respect des consignes..." className="flex-1" data-testid={`rubric-name-${i}`} />
                      <Input type="number" value={crit.max_points} onChange={(e) => updateCriterion(i, "max_points", Number(e.target.value))} className="w-20" data-testid={`rubric-points-${i}`} />
                      <Button size="icon" variant="ghost" type="button" onClick={() => removeCriterion(i)}><Trash size={16} className="text-accent" /></Button>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" type="button" onClick={addCriterion} className="mt-2" data-testid="add-rubric-criterion"><Plus size={14} className="mr-1" /> Ajouter un critère</Button>
                {form.rubric.length > 0 && <p className="mt-2 text-xs font-medium text-primary">Total : {rubricTotal} points</p>}
              </div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-assignment-btn">{editId ? "Enregistrer" : "Créer"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {items.length === 0 ? (
        <EmptyState
          icon={ClipboardText}
          title="Aucun devoir"
          description={canEdit ? "Créez un devoir pour évaluer vos étudiants sur ce cours." : "Aucun devoir n'a encore été publié pour ce cours."}
          actionLabel={canEdit ? "Nouveau devoir" : undefined}
          onAction={canEdit ? () => setOpen(true) : undefined}
        />
      ) : items.map((a) => (
        <div key={a.id} className="rounded-xl border border-border bg-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-display text-lg font-bold">{a.title}</h3>
                {a.group_mode && <Badge variant="secondary"><UsersThree size={12} className="mr-1" /> Devoir de groupe</Badge>}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{a.description}</p>
              <p className="mt-2 text-xs text-muted-foreground">{a.due_date ? `À rendre avant le ${a.due_date}` : "Sans date limite"} · /{a.max_points} points</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              {isStudent && (a.my_submission ? (
                a.my_submission.graded ? (
                  <Badge className="bg-emerald-600 hover:bg-emerald-600">Note : {a.my_submission.grade}/{a.max_points}</Badge>
                ) : (
                  <><Badge variant="secondary"><CheckCircle size={14} className="mr-1" /> Rendu</Badge>
                  <Button size="sm" variant="ghost" onClick={() => { setSubText(a.my_submission.content); setSubFile(null); setSubIsNew(false); setSubOpen(a.id); }}>Modifier</Button></>
                )
              ) : (
                <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => { setSubText(""); setSubFile(null); setSubIsNew(true); setSubOpen(a.id); }} data-testid={`submit-${a.id}`}>
                  <PaperPlaneTilt size={14} className="mr-1" /> Rendre
                </Button>
              ))}
              {isStudent && a.my_submission?.attachment_filename && (
                <button type="button" className="text-xs text-primary hover:underline" onClick={() => downloadAttachment(a.my_submission.id, a.my_submission.attachment_filename)}>
                  📎 {a.my_submission.attachment_filename}
                </button>
              )}
              {isStudent && a.my_submission?.group_id && (
                <p className="text-xs text-muted-foreground">Au nom de "{a.my_submission.group_name}" · rendu par {a.my_submission.submitted_by_name}</p>
              )}
              {canEdit && (
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => openGrade(a)} data-testid={`grade-open-${a.id}`}><Eye size={14} className="mr-1" /> Copies ({a.submissions_count || 0}/{a.participants_count ?? "?"})</Button>
                  <Button size="icon" variant="ghost" onClick={() => startEdit(a)} data-testid={`edit-assignment-${a.id}`}><PencilSimple size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(a)} data-testid={`del-assignment-${a.id}`}><Trash size={16} className="text-accent" /></Button>
                </div>
              )}
            </div>
          </div>
          {isStudent && a.my_submission?.graded && a.my_submission.criteria_grades && (
            <div className="mt-3 space-y-1 rounded-lg bg-secondary p-3 text-sm">
              <p className="mb-1 font-semibold">Détail de la note :</p>
              {a.my_submission.criteria_grades.map((cg) => {
                const crit = (a.rubric || []).find((r) => r.name === cg.name);
                return (
                  <div key={cg.name} className="flex items-center justify-between text-xs">
                    <span>{cg.name}</span>
                    <span className="font-medium">{cg.points} / {crit ? crit.max_points : "?"}</span>
                  </div>
                );
              })}
            </div>
          )}
          {isStudent && a.my_submission?.graded && a.my_submission.feedback && (
            <p className="mt-3 rounded-lg bg-secondary p-3 text-sm"><span className="font-semibold">Retour : </span>{a.my_submission.feedback}</p>
          )}
        </div>
      ))}

      {/* submit dialog */}
      <Dialog open={!!subOpen} onOpenChange={(o) => !o && setSubOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rendre le devoir</DialogTitle></DialogHeader>
          <Textarea rows={6} value={subText} onChange={(e) => setSubText(e.target.value)} placeholder="Votre réponse (optionnel, en complément du fichier)..." data-testid="submission-text" />
          <div>
            <Label>Pièce jointe (PDF ou Word) {subIsNew && <span className="text-accent">— obligatoire</span>}</Label>
            <Input type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => setSubFile(e.target.files[0] || null)} data-testid="submission-file" />
            {!subIsNew && !subFile && <p className="mt-1 text-xs text-muted-foreground">Laissez vide pour garder votre pièce jointe déjà envoyée.</p>}
          </div>
          <DialogFooter><Button onClick={() => submit(subOpen)} className="bg-primary hover:bg-primary/90" data-testid="submit-confirm">Envoyer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* grade dialog */}
      <Dialog open={!!gradeOpen} onOpenChange={(o) => !o && setGradeOpen(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>Copies — {gradeOpen?.title}</DialogTitle></DialogHeader>
          {gradeOpen && (subs.length === 0 ? <EmptyState icon={ClipboardText} title="Aucun étudiant concerné" description="Aucun étudiant n'a accès à ce cours pour l'instant." /> : subs.map((s) => (
            <GradeCard key={s.id || s.student_id || s.group_id} sub={s} max={gradeOpen.max_points} rubric={gradeOpen.rubric} onGraded={() => openGrade(gradeOpen)} onDownload={downloadAttachment} />
          )))}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GradeCard({ sub, max, rubric, onGraded, onDownload }) {
  const hasRubric = rubric && rubric.length > 0;
  const initialCriteria = hasRubric
    ? rubric.map((c) => {
        const existing = (sub.criteria_grades || []).find((cg) => cg.name === c.name);
        return { name: c.name, max_points: c.max_points, points: existing ? existing.points : "" };
      })
    : [];
  const [criteria, setCriteria] = useState(initialCriteria);
  const [grade, setGrade] = useState(sub.grade ?? "");
  const [feedback, setFeedback] = useState(sub.feedback ?? "");

  if (sub.pending) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-dashed border-border p-4 text-muted-foreground">
        <p className="font-medium">{sub.group_id ? `Groupe : ${sub.group_name}` : sub.student_name}</p>
        <Badge variant="secondary">En attente de remise</Badge>
      </div>
    );
  }

  const total = hasRubric ? criteria.reduce((s2, c) => s2 + (Number(c.points) || 0), 0) : Number(grade) || 0;

  const setCriterionPoints = (i, val) => setCriteria((cs) => cs.map((c, idx) => (idx === i ? { ...c, points: val } : c)));

  const save = async () => {
    try {
      if (hasRubric) {
        if (criteria.some((c) => c.points === "" || c.points === null)) { toast.error("Notez chaque critère avant d'enregistrer."); return; }
        await api.post(`/submissions/${sub.id}/grade`, {
          feedback,
          criteria_grades: criteria.map((c) => ({ name: c.name, points: Number(c.points) })),
        });
      } else {
        await api.post(`/submissions/${sub.id}/grade`, { grade: Number(grade), feedback });
      }
      toast.success("Note enregistrée"); onGraded();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold">{sub.group_id ? `Groupe : ${sub.group_name}` : sub.student_name}</p>
          {sub.group_id && <p className="text-xs text-muted-foreground">Rendu par {sub.submitted_by_name}</p>}
        </div>
        {sub.graded && <Badge className="bg-emerald-600 hover:bg-emerald-600">{sub.grade}/{max}</Badge>}
      </div>
      {sub.content && <p className="mt-2 whitespace-pre-wrap rounded bg-secondary p-3 text-sm">{sub.content}</p>}
      {sub.attachment_filename ? (
        <button type="button" className="mt-2 flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm text-primary hover:bg-secondary" onClick={() => onDownload(sub.id, sub.attachment_filename)} data-testid={`download-${sub.id}`}>
          <PaperPlaneTilt size={14} /> Télécharger la pièce jointe ({sub.attachment_filename})
        </button>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">Aucune pièce jointe.</p>
      )}

      {hasRubric ? (
        <div className="mt-3 space-y-2 rounded-lg border border-border p-3">
          <p className="text-xs font-medium text-muted-foreground">Grille de notation</p>
          {criteria.map((c, i) => (
            <div key={c.name} className="flex items-center justify-between gap-3 text-sm">
              <span className="flex-1">{c.name}</span>
              <div className="flex items-center gap-1">
                <Input type="number" min={0} max={c.max_points} value={c.points} onChange={(e) => setCriterionPoints(i, e.target.value)} className="h-8 w-16" data-testid={`rubric-grade-${sub.id}-${i}`} />
                <span className="text-xs text-muted-foreground">/ {c.max_points}</span>
              </div>
            </div>
          ))}
          <p className="border-t border-border pt-2 text-right text-sm font-display font-bold text-primary">Total : {total} / {max}</p>
        </div>
      ) : null}

      <div className="mt-3 flex items-end gap-2">
        {!hasRubric && (
          <div className="w-24"><Label>Note</Label><Input type="number" max={max} value={grade} onChange={(e) => setGrade(e.target.value)} data-testid={`grade-input-${sub.id}`} /></div>
        )}
        <div className="flex-1"><Label>Commentaire</Label><Input value={feedback} onChange={(e) => setFeedback(e.target.value)} /></div>
        <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid={`grade-save-${sub.id}`}>Noter</Button>
      </div>
    </div>
  );
}

const COVERS = [
  "https://images.pexels.com/photos/10744384/pexels-photo-10744384.jpeg",
  "https://images.pexels.com/photos/31468533/pexels-photo-31468533.jpeg",
  "https://images.pexels.com/photos/33865563/pexels-photo-33865563.jpeg",
];

function EditCourse({ course, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: course.title, description: course.description || "", code: course.code || "",
    filiere: course.filiere || "", category: course.category || "", cover_image: course.cover_image || "",
  });
  const [uploading, setUploading] = useState(false);
  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/courses/${course.id}/cover`, fd);
      setForm((f) => ({ ...f, cover_image: data.cover_image }));
      toast.success("Image mise à jour");
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };
  const save = async () => {
    try {
      await api.put(`/courses/${course.id}`, {
        ...form,
        published: course.published, meeting_url: course.meeting_url, group_ids: course.group_ids || [],
      });
      toast.success("Cours modifié");
      setOpen(false); onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="edit-course-btn"><PencilSimple size={16} className="mr-2" /> Modifier le cours</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Modifier le cours</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-course-title" /></div>
          <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="MGT-101" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="edit-course-desc" /></div>
          <div><Label>Filière</Label><Input value={form.filiere} onChange={(e) => setForm({ ...form, filiere: e.target.value })} /></div>
          <div>
            <Label>Image de couverture</Label>
            <div className="mt-2 flex items-center gap-3">
              {coverSrc(form.cover_image) ? (
                <img src={coverSrc(form.cover_image)} alt="Aperçu" className="h-16 w-24 rounded border border-border object-cover" />
              ) : (
                <CourseMonogram code={form.code} title={form.title} className="h-16 w-24 rounded border border-border" />
              )}
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({ ...form, cover_image: "" })} className={`h-14 w-20 overflow-hidden rounded border-2 ${!form.cover_image ? "border-primary" : "border-transparent"}`} title="Monogramme généré automatiquement">
                  <CourseMonogram code={form.code} title={form.title} className="h-full w-full" />
                </button>
                {COVERS.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, cover_image: c })} className={`h-14 w-20 overflow-hidden rounded border-2 ${form.cover_image === c ? "border-primary" : "border-transparent"}`}>
                    <img src={c} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-2">
              <Input type="file" accept=".jpg,.jpeg,.png,.webp,image/*" disabled={uploading} onChange={(e) => uploadCover(e.target.files[0])} data-testid="edit-course-cover-file" />
              {uploading && <p className="mt-1 text-xs text-muted-foreground">Envoi en cours...</p>}
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-edit-course-btn">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CourseSettings({ course, onSaved }) {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(course.group_ids || []);
  const [meeting, setMeeting] = useState(course.meeting_url || "");
  const [open, setOpen] = useState(false);
  useEffect(() => { if (open) api.get("/groups").then((r) => setGroups(r.data)).catch(() => {}); }, [open]);
  const toggle = (gid) => setSelected((s) => (s.includes(gid) ? s.filter((x) => x !== gid) : [...s, gid]));
  const save = async () => {
    try {
      await api.post(`/courses/${course.id}/groups`, { group_ids: selected });
      await api.put(`/courses/${course.id}`, {
        title: course.title, description: course.description, code: course.code,
        filiere: course.filiere, category: course.category, cover_image: course.cover_image,
        published: course.published, meeting_url: meeting, group_ids: selected,
      });
      toast.success("Paramètres enregistrés");
      setOpen(false); onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="course-settings-btn"><UsersThree size={16} className="mr-2" /> Accès & salle virtuelle</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Accès du cours & salle virtuelle</DialogTitle></DialogHeader>
        <div>
          <Label>Lien de visioconférence</Label>
          <Input value={meeting} onChange={(e) => setMeeting(e.target.value)} placeholder="https://meet.jit.si/..." data-testid="meeting-input" />
        </div>
        <div>
          <Label className="mb-2 block">Groupes ayant accès à ce cours</Label>
          {groups.length === 0 && <p className="text-sm text-muted-foreground">Aucun groupe disponible. L'administrateur doit d'abord créer des groupes.</p>}
          <div className="space-y-2">
            {groups.map((g) => (
              <label key={g.id} className="flex cursor-pointer items-center gap-3 rounded-lg border border-border p-3 text-sm" data-testid={`grp-opt-${g.id}`}>
                <Checkbox checked={selected.includes(g.id)} onCheckedChange={() => toggle(g.id)} />
                <span className="font-medium">{g.name}</span>
                <Badge variant="secondary">{g.member_count} étud.</Badge>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-access-btn">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- QUIZZES ---------------- */
function Quizzes({ courseId, canEdit, isStudent }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [availableFrom, setAvailableFrom] = useState("");
  const [questions, setQuestions] = useState([{ question: "", options: ["", "", "", ""], correct_index: 0 }]);
  const [take, setTake] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  const load = () => api.get(`/courses/${courseId}/quizzes`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [courseId]);

  const save = async () => {
    try { await api.post(`/courses/${courseId}/quizzes`, { title, description: "", questions, due_date: dueDate || null, available_from: availableFrom || null }); toast.success("Quiz créé"); setOpen(false); setTitle(""); setDueDate(""); setAvailableFrom(""); setQuestions([{ question: "", options: ["", "", "", ""], correct_index: 0 }]); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const startTake = (q) => { setTake(q); setAnswers(new Array(q.questions.length).fill(-1)); setResult(null); };
  const submitTake = async () => {
    try { const { data } = await api.post(`/quizzes/${take.id}/attempt`, { answers: answers.map((a) => (a < 0 ? 0 : a)) }); setResult(data); toast.success(`Score : ${data.score}/${data.total}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      {canEdit && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button variant="outline" data-testid="add-quiz-btn"><Plus size={16} className="mr-2" /> Nouveau quiz</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
            <DialogHeader><DialogTitle>Créer un quiz</DialogTitle></DialogHeader>
            <div><Label>Titre</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="quiz-title" /></div>
            <div><Label>Date limite (optionnel)</Label><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="quiz-due-date" /></div>
            <div><Label>Disponible à partir du (optionnel)</Label><Input type="date" value={availableFrom} onChange={(e) => setAvailableFrom(e.target.value)} data-testid="quiz-available-from" /></div>
            {questions.map((q, qi) => (
              <div key={qi} className="rounded-lg border border-border p-3">
                <Input value={q.question} onChange={(e) => { const n = [...questions]; n[qi].question = e.target.value; setQuestions(n); }} placeholder={`Question ${qi + 1}`} className="font-medium" />
                {q.options.map((o, oi) => (
                  <div key={oi} className="mt-2 flex items-center gap-2">
                    <input type="radio" checked={q.correct_index === oi} onChange={() => { const n = [...questions]; n[qi].correct_index = oi; setQuestions(n); }} />
                    <Input value={o} onChange={(e) => { const n = [...questions]; n[qi].options[oi] = e.target.value; setQuestions(n); }} placeholder={`Option ${oi + 1}`} />
                  </div>
                ))}
              </div>
            ))}
            <Button variant="ghost" onClick={() => setQuestions([...questions, { question: "", options: ["", "", "", ""], correct_index: 0 }])}><Plus size={14} className="mr-1" /> Ajouter une question</Button>
            <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-quiz-btn">Enregistrer le quiz</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {items.length === 0 ? (
        <EmptyState
          icon={Question}
          title="Aucun quiz"
          description={canEdit ? "Créez un quiz pour tester les connaissances de vos étudiants." : "Aucun quiz n'a encore été publié pour ce cours."}
          actionLabel={canEdit ? "Nouveau quiz" : undefined}
          onAction={canEdit ? () => setOpen(true) : undefined}
        />
      ) : items.map((q) => (
        <div key={q.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-5">
          <div>
            <h3 className="font-display text-lg font-bold">{q.title}</h3>
            <p className="text-sm text-muted-foreground">{q.questions?.length || 0} questions</p>
            {isStudent && q.locked && (
              <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Lock size={12} /> Disponible à partir du {q.available_from}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isStudent && q.my_attempt && <Badge className="bg-emerald-600 hover:bg-emerald-600">{q.my_attempt.score}/{q.my_attempt.total}</Badge>}
            {isStudent && (
              q.locked ? (
                <Button size="sm" variant="outline" disabled data-testid={`take-quiz-${q.id}`}><Lock size={14} className="mr-2" /> Verrouillé</Button>
              ) : (
                <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => startTake(q)} data-testid={`take-quiz-${q.id}`}>{q.my_attempt ? "Refaire" : "Passer"}</Button>
              )
            )}
            {canEdit && <Button size="icon" variant="ghost" onClick={async () => { await api.delete(`/quizzes/${q.id}`); load(); }}><Trash size={16} /></Button>}
          </div>
        </div>
      ))}

      {/* take quiz */}
      <Dialog open={!!take} onOpenChange={(o) => !o && setTake(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{take?.title}</DialogTitle></DialogHeader>
          {take?.questions.map((q, qi) => (
            <div key={qi} className="rounded-lg border border-border p-4">
              <p className="font-medium">{qi + 1}. {q.question}</p>
              <div className="mt-2 space-y-2">
                {q.options.map((o, oi) => {
                  const correct = result && result.corrections[qi].correct_index === oi;
                  const chosen = answers[qi] === oi;
                  return (
                    <label key={oi} className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2 text-sm ${result ? (correct ? "border-emerald-500 bg-emerald-50" : chosen ? "border-accent bg-red-50" : "border-border") : chosen ? "border-primary bg-primary/5" : "border-border"}`}>
                      <input type="radio" disabled={!!result} checked={chosen} onChange={() => { const n = [...answers]; n[qi] = oi; setAnswers(n); }} data-testid={`quiz-q${qi}-o${oi}`} />
                      {o}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
          {result ? (
            <div className="rounded-lg bg-primary p-4 text-center text-primary-foreground">
              <p className="font-display text-2xl font-extrabold">{result.score}/{result.total}</p>
              <p className="text-sm">Résultat du quiz</p>
            </div>
          ) : (
            <DialogFooter><Button onClick={submitTake} className="bg-primary hover:bg-primary/90" data-testid="quiz-submit">Valider mes réponses</Button></DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- FORUM ---------------- */
function Forum({ courseId }) {
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = () => api.get(`/courses/${courseId}/forum`).then((r) => setPosts(r.data));
  useEffect(() => { load(); }, [courseId]);

  const post = async () => {
    if (!content.trim()) return;
    try { await api.post(`/courses/${courseId}/forum`, { title, content }); setContent(""); setTitle(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reply = async (topicId) => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/courses/${courseId}/forum`, { content: replyText, parent_id: topicId });
      setReplyText(""); setReplyOpen(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const topics = posts.filter((p) => !p.parent_id);
  const repliesOf = (topicId) => posts.filter((p) => p.parent_id === topicId);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-5">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Sujet (optionnel)" className="mb-2" data-testid="forum-title" />
        <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Écrivez votre message..." data-testid="forum-input" />
        <Button onClick={post} className="mt-3 bg-primary hover:bg-primary/90" data-testid="forum-post-btn"><PaperPlaneTilt size={16} className="mr-2" /> Publier</Button>
      </div>
      {topics.length === 0 ? <EmptyState icon={ChatsCircle} title="Aucune discussion" description="Soyez le premier à lancer un sujet dans ce forum." /> : topics.map((p) => (
        <div key={p.id} className="rounded-xl border border-border bg-card p-5" data-testid={`forum-topic-${p.id}`}>
          <ForumAuthor p={p} />
          {p.title && <p className="mt-2 font-display font-bold">{p.title}</p>}
          <p className="mt-1 whitespace-pre-wrap text-sm">{p.content}</p>

          {repliesOf(p.id).length > 0 && (
            <div className="mt-4 ml-4 space-y-3 border-l-2 border-border pl-4">
              {repliesOf(p.id).map((r) => (
                <div key={r.id} data-testid={`forum-reply-${r.id}`}>
                  <ForumAuthor p={r} small />
                  <p className="mt-1 whitespace-pre-wrap text-sm">{r.content}</p>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4">
            {replyOpen === p.id ? (
              <div className="flex items-start gap-2">
                <Textarea rows={2} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Votre réponse..." className="flex-1" data-testid={`forum-reply-input-${p.id}`} />
                <div className="flex flex-col gap-1">
                  <Button size="sm" onClick={() => reply(p.id)} className="bg-primary hover:bg-primary/90" data-testid={`forum-reply-send-${p.id}`}>Envoyer</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setReplyOpen(null); setReplyText(""); }}>Annuler</Button>
                </div>
              </div>
            ) : (
              <button type="button" className="flex items-center gap-1 text-xs font-medium text-primary hover:underline" onClick={() => { setReplyOpen(p.id); setReplyText(""); }} data-testid={`forum-reply-btn-${p.id}`}>
                <ChatsCircle size={14} /> Répondre {repliesOf(p.id).length > 0 && `(${repliesOf(p.id).length})`}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ForumAuthor({ p, small }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`flex items-center justify-center rounded-full bg-primary font-bold text-primary-foreground ${small ? "h-6 w-6 text-[10px]" : "h-8 w-8 text-xs"}`}>{p.author_name[0]}</div>
      <div>
        <div className="text-sm font-semibold">{p.author_name} <Badge variant="secondary" className="ml-1 text-[10px]">{p.author_role}</Badge></div>
        <p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString("fr-FR")}</p>
      </div>
    </div>
  );
}


